import { useMemo, useState, type ReactElement } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Plus, RefreshCw, Upload } from 'lucide-react'

import { logger } from '~/lib/logger'
import { DataTablePagination } from '~/components/data/DataTablePagination'
import {
  ArchiveArticleDialog,
  ArticlesTable,
  BulkActionDialog,
  ChunksPreviewDrawer,
  DeleteArticleDialog,
  EMPTY_HELP_FILTERS,
  HelpFilterStrip,
  ImportArticlesDialog,
  PublishDialog,
  UnpublishDialog,
  articlesQuery,
  chunksQuery,
  hasActiveFilters,
  reindexBanner,
  reindexButtonLabel,
  useArchiveArticle,
  useBulkArticleAction,
  useDeleteArticle,
  useImportArticles,
  usePublishArticle,
  useReindex,
  useUnpublishArticle,
  type BulkArticleAction,
  type HelpArticleRow,
  type HelpFilterValue,
} from '~/features/help-kb'

const DEFAULT_PAGE = 1
const DEFAULT_LIMIT = 25

/**
 * The article list, and every action that changes what the Assistant can say.
 * Writing/editing an article is its own page (`admin.help-kb.new.tsx`,
 * `admin.help-kb.$articleId.edit.tsx`) — everything else here is still a
 * lightweight dialog, the same way `admin.feature-flags.tsx` keys its
 * dialogs by id: a refetch replaces the row objects, and a dialog holding a
 * stale one would act on old content.
 */
export const Route = createFileRoute('/admin/help-kb/')({
  component: HelpKbArticlesPage,
})

const EMPTY_ARTICLES: readonly HelpArticleRow[] = []
const EMPTY_SELECTION: ReadonlySet<string> = new Set()

function HelpKbArticlesPage(): ReactElement {
  const navigate = useNavigate()
  const [filters, setFiltersRaw] = useState<HelpFilterValue>(EMPTY_HELP_FILTERS)
  const [page, setPage] = useState(DEFAULT_PAGE)
  const [limit, setLimit] = useState(DEFAULT_LIMIT)
  // A selection is of rows on the current page; a different page or filter
  // shows different rows, so it starts empty rather than carrying hidden ids.
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(EMPTY_SELECTION)
  const [bulkFor, setBulkFor] = useState<BulkArticleAction | null>(null)

  // A filter change re-scopes the corpus a page number was picked against, so
  // page 3 of "all modules" is meaningless once "billing" is selected. Reset
  // to page 1 rather than clamp — the clamp in DataTablePagination is for a
  // limit change, not a change in what's being counted.
  const setFilters = (next: HelpFilterValue): void => {
    setFiltersRaw(next)
    setPage(DEFAULT_PAGE)
    setSelectedIds(EMPTY_SELECTION)
  }
  const [publishFor, setPublishFor] = useState<string | null>(null)
  const [unpublishFor, setUnpublishFor] = useState<string | null>(null)
  const [archiveFor, setArchiveFor] = useState<string | null>(null)
  const [deleteFor, setDeleteFor] = useState<string | null>(null)
  const [chunksFor, setChunksFor] = useState<string | null>(null)
  const [importOpen, setImportOpen] = useState(false)

  const list = useQuery(articlesQuery({
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.module ? { module: filters.module } : {}),
    ...(filters.kind ? { kind: filters.kind } : {}),
    ...(filters.search ? { q: filters.search } : {}),
    limit,
    offset: (page - 1) * limit,
  }))

  const publishMutation = usePublishArticle()
  const unpublishMutation = useUnpublishArticle()
  const archiveMutation = useArchiveArticle()
  const deleteMutation = useDeleteArticle()
  const importMutation = useImportArticles()
  const reindexMutation = useReindex()
  const bulkMutation = useBulkArticleAction()

  const reindexSummary = reindexMutation.data ? reindexBanner(reindexMutation.data) : null

  const articles = list.data?.articles ?? EMPTY_ARTICLES
  const byId = useMemo(() => new Map(articles.map((article) => [article.id, article])), [articles])

  const chunks = useQuery({ ...chunksQuery(chunksFor ?? ''), enabled: chunksFor !== null })

  const mutationError =
    publishMutation.error ??
    unpublishMutation.error ??
    archiveMutation.error ??
    deleteMutation.error ??
    reindexMutation.error ??
    bulkMutation.error

  // Resolved once each: a dialog renders only when its article is still in the
  // list, so a refetch that drops the row closes the dialog instead of throwing.
  const publishing = publishFor ? byId.get(publishFor) ?? null : null
  const unpublishing = unpublishFor ? byId.get(unpublishFor) ?? null : null
  const archiving = archiveFor ? byId.get(archiveFor) ?? null : null
  const deleting = deleteFor ? byId.get(deleteFor) ?? null : null
  const previewing = chunksFor ? byId.get(chunksFor) ?? null : null

  // The dialog acts on the rows as they were when it was confirmed: the refetch
  // after each article replaces the list, and a published row would otherwise
  // drop out of a "publish" selection mid-run.
  const bulkArticles = bulkMutation.variables?.articles ?? articles.filter((article) => selectedIds.has(article.id))
  const closeBulk = (): void => {
    setBulkFor(null)
    if (bulkMutation.data) {
      bulkMutation.reset()
      setSelectedIds(EMPTY_SELECTION)
    }
  }

  const goToNew = () => void navigate({ to: '/admin/help-kb/new' })
  const goToEdit = (articleId: string) =>
    void navigate({ to: '/admin/help-kb/$articleId/edit', params: { articleId } })

  return (
    <>
      <HelpFilterStrip value={filters} onChange={setFilters} modules={[...new Set(articles.map((article) => article.module))].sort()} />

      <div className="ka-flags-actions">
        <button
          type="button"
          className="ka-btn ka-btn--secondary ka-btn--sm"
          onClick={() => reindexMutation.mutate(undefined, {
            // A run that stops part-way RESOLVES with its partial counts (the
            // batches before the failure really did upload), so it is logged
            // here; onError only sees a failure before any batch answered.
            onSuccess: (summary) => {
              if (!summary.completed) {
                logger.warn('help-kb.reindex.partial', {
                  processed: summary.processed,
                  total: summary.total,
                  error: summary.error,
                })
              }
            },
            onError: (cause) => logger.warn('help-kb.reindex.failed', { cause }),
          })}
          disabled={reindexMutation.isPending}
        >
          <RefreshCw size={12} aria-hidden="true" /> {reindexButtonLabel(reindexMutation.isPending, reindexMutation.progress)}
        </button>
        <button type="button" className="ka-btn ka-btn--secondary ka-btn--sm" onClick={() => setImportOpen(true)}>
          <Upload size={12} aria-hidden="true" /> Import .md
        </button>
        <button type="button" className="ka-btn ka-btn--primary" onClick={goToNew}>
          <Plus size={14} aria-hidden="true" /> New article
        </button>
      </div>

      {reindexSummary ? (
        <div className={`ka-banner ${reindexSummary.tone === 'error' ? 'is-error' : 'is-tip'}`} role="status">
          {reindexSummary.text}
        </div>
      ) : null}

      {publishMutation.data && !publishMutation.data.indexed ? (
        <div className="ka-banner is-error" role="alert">
          Published, but the search index rejected it: {publishMutation.data.index_error}. The article is live in this
          list and invisible to the Assistant until a reindex succeeds.
        </div>
      ) : null}

      {mutationError ? (
        <div role="alert" className="ka-inline-error">
          {mutationError instanceof Error ? mutationError.message : 'The change could not be saved.'}
        </div>
      ) : null}

      <ArticlesTable
        articles={articles}
        isLoading={list.isLoading}
        error={list.error}
        hasFilters={hasActiveFilters(filters)}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        onRowClick={(article) => goToEdit(article.id)}
        onClearFilters={() => setFilters(EMPTY_HELP_FILTERS)}
        onCreate={goToNew}
        onPublish={(article) => setPublishFor(article.id)}
        onUnpublish={(article) => setUnpublishFor(article.id)}
        onArchive={(article) => setArchiveFor(article.id)}
        onDelete={(article) => setDeleteFor(article.id)}
        onPreviewChunks={(article) => setChunksFor(article.id)}
        onBulkAction={(action) => setBulkFor(action)}
      />

      <DataTablePagination
        page={page}
        limit={limit}
        total={list.data?.total ?? 0}
        isLoading={list.isLoading}
        onChange={({ page: nextPage, limit: nextLimit }) => {
          setPage(nextPage)
          setLimit(nextLimit)
          setSelectedIds(EMPTY_SELECTION)
        }}
      />

      {publishing ? (
        <PublishDialog
          article={publishing}
          onClose={() => setPublishFor(null)}
          isSubmitting={publishMutation.isPending}
          onConfirm={() => publishMutation.mutate({ id: publishing.id }, { onSuccess: () => setPublishFor(null) })}
        />
      ) : null}

      {unpublishing ? (
        <UnpublishDialog
          article={unpublishing}
          onClose={() => setUnpublishFor(null)}
          isSubmitting={unpublishMutation.isPending}
          onConfirm={() => unpublishMutation.mutate({ id: unpublishing.id }, { onSuccess: () => setUnpublishFor(null) })}
        />
      ) : null}

      {archiving ? (
        <ArchiveArticleDialog
          article={archiving}
          onClose={() => setArchiveFor(null)}
          isSubmitting={archiveMutation.isPending}
          onConfirm={(reason) => archiveMutation.mutate({ id: archiving.id, reason }, { onSuccess: () => setArchiveFor(null) })}
        />
      ) : null}

      {deleting ? (
        <DeleteArticleDialog
          article={deleting}
          onClose={() => setDeleteFor(null)}
          isSubmitting={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate({ id: deleting.id }, { onSuccess: () => setDeleteFor(null) })}
        />
      ) : null}

      {bulkFor ? (
        <BulkActionDialog
          action={bulkFor}
          articles={bulkArticles}
          isRunning={bulkMutation.isPending}
          progress={bulkMutation.progress}
          summary={bulkMutation.data ?? null}
          onClose={closeBulk}
          onConfirm={(reason) =>
            bulkMutation.mutate(
              { action: bulkFor, articles: bulkArticles, ...(reason === undefined ? {} : { reason }) },
              {
                onSuccess: (summary) => {
                  if (summary.failed.length > 0 || summary.notIndexed.length > 0) {
                    logger.warn('help-kb.bulk.partial', {
                      action: summary.action,
                      total: summary.total,
                      failed: summary.failed.length,
                      notIndexed: summary.notIndexed.length,
                    })
                  }
                },
                onError: (cause) => logger.warn('help-kb.bulk.failed', { action: bulkFor, cause }),
              },
            )
          }
        />
      ) : null}

      {previewing ? (
        <ChunksPreviewDrawer
          article={previewing}
          chunks={chunks.data?.chunks ?? []}
          indexed={chunks.data?.indexed ?? false}
          reason={chunks.data?.reason ?? null}
          isLoading={chunks.isLoading}
          onClose={() => setChunksFor(null)}
        />
      ) : null}

      {importOpen ? (
        <ImportArticlesDialog
          onClose={() => { setImportOpen(false); importMutation.reset() }}
          isSubmitting={importMutation.isPending}
          error={importMutation.error}
          result={importMutation.data ?? null}
          onSubmit={({ files, validateOnly }) => importMutation.mutate({ files, validateOnly })}
        />
      ) : null}
    </>
  )
}
